import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, RotateCcw, Download, Check } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import PromoBanner from "@/components/site/PromoBanner";
import SEO from "@/components/seo/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: "task",
    q: "What do you spend the most time on?",
    options: [
      { v: "writing", label: "Writing — emails, captions, posts" },
      { v: "planning", label: "Planning — weeks, projects, habits" },
      { v: "summarising", label: "Summarising — articles, meetings, books" },
      { v: "shipping", label: "Shipping content end-to-end" },
    ],
  },
  {
    id: "team",
    q: "Solo or working with others?",
    options: [
      { v: "solo", label: "Just me" },
      { v: "small", label: "Small team / co-founder" },
    ],
  },
  {
    id: "stack",
    q: "Where do you mostly work?",
    options: [
      { v: "ai", label: "AI tools (ChatGPT, Claude, Gemini)" },
      { v: "notion", label: "Notion" },
      { v: "anywhere", label: "Anywhere — text is text" },
    ],
  },
];

const recommend = (a) => {
  if (a.task === "summarising") return "summary-prompts-pack";
  if (a.task === "planning" || a.stack === "notion") return "notion-planner-kit";
  if (a.task === "shipping") return "content-workflow-system";
  if (a.task === "writing" && a.stack === "ai") return "ai-writing-kit";
  return "vaymilo-starter-bundle";
};

const PRODUCT_META = {
  "ai-writing-kit": { name: "AI Writing Kit", price: "$19", why: "You write a lot and you live in AI tools — this kit hands you 100+ tested prompts plus calm daily workflows." },
  "notion-planner-kit": { name: "Notion Planner Kit", price: "$15", why: "You plan your work in Notion — this kit drops in a calm weekly + daily + habits system, no influencer aesthetics." },
  "summary-prompts-pack": { name: "Summary Prompts Pack", price: "$9", why: "You read and summarise a lot — 50 hand-tested prompts with length and tone presets do exactly one job, well." },
  "content-workflow-system": { name: "Content Workflow System", price: "$29", why: "You ship content end-to-end — a 5-stage workflow turns staring-at-blank-document into a repeatable process." },
  "vaymilo-starter-bundle": { name: "Vaymilo Starter Bundle", price: "$35", why: "You touch several modes of work — get the three core kits together and save $8." },
};

const Start = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({ first_name: "", email: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(null); // { first_name, slug }

  const total = QUESTIONS.length;
  const done = step >= total;
  const slug = done ? recommend(answers) : null;
  const meta = slug ? PRODUCT_META[slug] : null;

  const pick = (qid, v) => {
    setAnswers({ ...answers, [qid]: v });
    setStep((s) => s + 1);
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
    setLead({ first_name: "", email: "", website: "" });
    setSent(null);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.first_name.trim() || !lead.email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quiz/complete`, {
        first_name: lead.first_name.trim(),
        email: lead.email.trim(),
        slug,
        answers,
        website: lead.website,
      });
      const pdfUrl = `${API}/pdf/${slug}?name=${encodeURIComponent(lead.first_name.trim())}`;
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
      setSent({ first_name: lead.first_name.trim(), slug, pdfUrl });
      toast.success("Your starter plan is on its way.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send. Try again in a minute.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="start-page" className="min-h-screen bg-[#F7F7F4] flex flex-col">
      <SEO title="Which kit fits me?" description="Answer 3 quick questions and get a personalized 1-page starter plan PDF." path="/start" />
      <PromoBanner />
      <Header />

      <section className="bg-[#F7F7F4] border-b border-[#E7E7E2]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
          <div className="overline">— Which kit fits me?</div>
          <h1 className="mt-5 font-sora text-[40px] sm:text-[54px] lg:text-[64px] font-extrabold tracking-tight leading-[1.05] text-[#111111]">
            {done ? "Here's what fits." : "3 quick questions."}
          </h1>
          {!done && (
            <p className="mt-5 font-manrope text-[16px] md:text-[17px] text-[#111111]/65">
              No spam. We&rsquo;ll recommend one kit and send you a 1-page personalized plan.
            </p>
          )}
        </div>
      </section>

      <section className="flex-1 bg-white border-b border-[#E7E7E2]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
          {!done ? (
            <>
              <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#111111]/45">
                Question {step + 1} of {total}
              </div>
              <h2 data-testid={`quiz-q-${step}`} className="mt-3 font-sora text-[26px] md:text-[32px] font-bold text-[#111111] tracking-tight">
                {QUESTIONS[step].q}
              </h2>
              <div className="mt-8 space-y-3">
                {QUESTIONS[step].options.map((o) => (
                  <button
                    key={o.v}
                    onClick={() => pick(QUESTIONS[step].id, o.v)}
                    data-testid={`quiz-opt-${o.v}`}
                    className="w-full text-left bg-white border border-[#E7E7E2] rounded-xl px-5 py-4 font-manrope text-[15px] md:text-[16px] text-[#111111] hover:border-[#2563EB] hover:bg-[#F7F7F4] transition-colors flex items-center justify-between group"
                  >
                    <span>{o.label}</span>
                    <ArrowUpRight size={16} className="text-[#111111]/30 group-hover:text-[#2563EB] transition-colors" />
                  </button>
                ))}
              </div>
              <div className="mt-10 h-1 w-full bg-[#E7E7E2] rounded-full overflow-hidden">
                <div className="h-full bg-[#2563EB] transition-all" style={{ width: `${(step / total) * 100}%` }} />
              </div>
            </>
          ) : (
            <div data-testid="quiz-result">
              <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#2563EB]">
                We recommend
              </div>
              <h2 className="mt-3 font-sora text-[36px] sm:text-[48px] font-extrabold text-[#111111] tracking-tight">
                {meta.name}.
              </h2>
              <p className="mt-5 max-w-xl font-manrope text-[16px] md:text-[17px] text-[#111111]/75 leading-relaxed">
                {meta.why}
              </p>

              {/* Lead magnet — personalized PDF capture */}
              <div className="mt-10 border border-[#E7E7E2] rounded-2xl bg-[#F7F7F4] p-6 md:p-8">
                {!sent ? (
                  <>
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#2563EB]">
                      — Free starter plan PDF
                    </div>
                    <h3 className="mt-3 font-sora text-[22px] md:text-[26px] font-bold text-[#111111] tracking-tight">
                      Get a 1-page plan with your name on it.
                    </h3>
                    <p className="mt-2 font-manrope text-[14px] md:text-[15px] text-[#111111]/65 max-w-xl leading-relaxed">
                      We&rsquo;ll personalize a PDF cover for {meta.name} and email you occasional updates when new kits drop. No spam.
                    </p>
                    <form onSubmit={submitLead} className="mt-6 grid grid-cols-1 sm:grid-cols-12 gap-3" data-testid="quiz-lead-form">
                      {/* Honeypot */}
                      <div className="vaymilo-honeypot" aria-hidden="true">
                        <label htmlFor="qz-website">Leave blank</label>
                        <input
                          id="qz-website"
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          value={lead.website}
                          onChange={(e) => setLead({ ...lead, website: e.target.value })}
                        />
                      </div>
                      <input
                        required
                        data-testid="quiz-lead-name"
                        type="text"
                        placeholder="First name"
                        value={lead.first_name}
                        onChange={(e) => setLead({ ...lead, first_name: e.target.value })}
                        className="sm:col-span-4 bg-white border border-[#E7E7E2] rounded-lg px-4 py-3 font-manrope text-[15px] text-[#111111] placeholder:text-[#111111]/40 focus:outline-none focus:border-[#2563EB]"
                      />
                      <input
                        required
                        data-testid="quiz-lead-email"
                        type="email"
                        placeholder="you@studio.com"
                        value={lead.email}
                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                        className="sm:col-span-5 bg-white border border-[#E7E7E2] rounded-lg px-4 py-3 font-manrope text-[15px] text-[#111111] placeholder:text-[#111111]/40 focus:outline-none focus:border-[#2563EB]"
                      />
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="quiz-lead-submit"
                        className="sm:col-span-3 inline-flex items-center justify-center gap-2 bg-[#111111] hover:bg-[#2563EB] disabled:opacity-60 text-white font-manrope font-medium text-[15px] px-5 py-3 rounded-lg transition-colors"
                      >
                        <Download size={14} />
                        {submitting ? "Sending…" : "Get PDF"}
                      </button>
                    </form>
                  </>
                ) : (
                  <div data-testid="quiz-lead-success">
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#2563EB] inline-flex items-center gap-2">
                      <Check size={12} /> Done
                    </div>
                    <h3 className="mt-3 font-sora text-[22px] md:text-[26px] font-bold text-[#111111] tracking-tight">
                      Your plan is ready, {sent.first_name}.
                    </h3>
                    <p className="mt-2 font-manrope text-[14px] md:text-[15px] text-[#111111]/65 max-w-xl leading-relaxed">
                      A new tab just opened with your personalized PDF. If your browser blocked it, use the link below.
                    </p>
                    <a
                      href={sent.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="quiz-lead-pdf-link"
                      className="mt-4 inline-flex items-center gap-2 bg-white text-[#111111] font-manrope font-medium text-[14px] px-4 py-2.5 rounded-lg border border-[#E7E7E2] hover:border-[#111111] transition-colors"
                    >
                      <Download size={14} /> Open PDF
                    </a>
                  </div>
                )}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to={`/products/${slug}`}
                  data-testid="quiz-result-cta"
                  className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg transition-colors"
                >
                  View {meta.name} · {meta.price}
                  <ArrowUpRight size={16} />
                </Link>
                <button
                  onClick={reset}
                  data-testid="quiz-restart"
                  className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg border border-[#E7E7E2] hover:border-[#111111] transition-colors"
                >
                  <RotateCcw size={14} /> Restart
                </button>
              </div>
              <Link to="/products" className="mt-8 inline-block font-manrope text-sm text-[#111111]/65 hover:text-[#111111] transition-colors border-b border-[#E7E7E2] hover:border-[#111111] pb-0.5">
                Or browse all products →
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Start;
